import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Login from "@/pages/Login";
import SuperAdmin from "@/pages/SuperAdmin";
import CmsLayout from "@/pages/CmsLayout";
import CmsDashboard from "@/pages/CmsDashboard";
import CmsPages from "@/pages/CmsPages";
import PageBuilder from "@/pages/PageBuilder";
import ThemeStudio from "@/pages/ThemeStudio";
import HeaderBuilder from "@/pages/HeaderBuilder";
import FooterBuilder from "@/pages/FooterBuilder";
import BlogManager from "@/pages/BlogManager";
import GalleryManager from "@/pages/GalleryManager";
import SeoManager from "@/pages/SeoManager";
import FormBuilder from "@/pages/FormBuilder";
import ServicesManager from "@/pages/ServicesManager";
import BookingsManager from "@/pages/BookingsManager";
import NewsletterManager from "@/pages/NewsletterManager";
import StaffManager from "@/pages/StaffManager";
import AvailabilityManager from "@/pages/AvailabilityManager";
import EmailSettings from "@/pages/EmailSettings";
import EmailTemplates from "@/pages/EmailTemplates";
import SuperEmailSettings from "@/pages/SuperEmailSettings";
import SuperEmailTemplates from "@/pages/SuperEmailTemplates";
import SettingsPage from "@/pages/SettingsPage";
import OnboardingWizard from "@/pages/OnboardingWizard";
import CustomerPortal from "@/pages/CustomerPortal";
import LoyaltyManager from "@/pages/LoyaltyManager";
import AnalyticsPage from "@/pages/AnalyticsPage";
import BuilderPreview from "@/pages/BuilderPreview";
import UpgradeRequired from "@/pages/UpgradeRequired";
import PlansManager from "@/pages/PlansManager";
import IndustriesManager from "@/pages/IndustriesManager";
import LandingPage from "@/pages/LandingPage";
import LandingEditor from "@/pages/LandingEditor";
import StripeSettings from "@/pages/StripeSettings";
import CustomersManager from "@/pages/CustomersManager";
import PlatformPagesManager from "@/pages/PlatformPagesManager";
import PlatformPageBuilder from "@/pages/PlatformPageBuilder";
import PublicPlatformPage from "@/pages/PublicPlatformPage";
import StudioMVBadge from "@/components/StudioMVBadge";
import CookieConsent from "@/components/CookieConsent";
import { ForgotPasswordPage, ResetPasswordPage } from "@/pages/PasswordReset";
import PricingPage from "@/pages/PricingPage";
import RegisterPage from "@/pages/RegisterPage";
import RegisterVerifyPage from "@/pages/RegisterVerifyPage";
import PublicSalon, { BlogPostPage } from "@/pages/PublicSalon";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ color: "var(--mv-text-muted)" }}>Lade ...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/cms" replace />;
  return children;
}

function FeatureRoute({ feature, children }) {
  const { hasFeature, loading } = useAuth();
  if (loading) return null;
  if (!hasFeature(feature)) return <UpgradeRequired feature={feature} />;
  return children;
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "super_admin" ? "/admin" : "/cms"} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/salon/:slug/reset-password" element={<ResetPasswordPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/register/verify/:token" element={<RegisterVerifyPage />} />
          <Route path="/admin" element={<Protected role="super_admin"><SuperAdmin /></Protected>} />
          <Route path="/admin/plans" element={<Protected role="super_admin"><PlansManager /></Protected>} />
          <Route path="/admin/industries" element={<Protected role="super_admin"><IndustriesManager /></Protected>} />
          <Route path="/admin/landing" element={<Protected role="super_admin"><LandingEditor /></Protected>} />
          <Route path="/admin/stripe" element={<Protected role="super_admin"><StripeSettings /></Protected>} />
          <Route path="/admin/email-settings" element={<Protected role="super_admin"><SuperEmailSettings /></Protected>} />
          <Route path="/admin/email-templates" element={<Protected role="super_admin"><SuperEmailTemplates /></Protected>} />
          <Route path="/admin/pages" element={<Protected role="super_admin"><PlatformPagesManager /></Protected>} />
          <Route path="/admin/pages/:id" element={<Protected role="super_admin"><PlatformPageBuilder /></Protected>} />

          <Route path="/cms" element={<Protected><CmsLayout /></Protected>}>
            <Route index element={<CmsDashboard />} />
            <Route path="pages" element={<FeatureRoute feature="cms_pages"><CmsPages /></FeatureRoute>} />
            <Route path="theme" element={<FeatureRoute feature="theme_studio"><ThemeStudio /></FeatureRoute>} />
            <Route path="header" element={<FeatureRoute feature="header_footer"><HeaderBuilder /></FeatureRoute>} />
            <Route path="footer" element={<FeatureRoute feature="header_footer"><FooterBuilder /></FeatureRoute>} />
            <Route path="blog" element={<FeatureRoute feature="blog"><BlogManager /></FeatureRoute>} />
            <Route path="gallery" element={<FeatureRoute feature="gallery"><GalleryManager /></FeatureRoute>} />
            <Route path="services" element={<FeatureRoute feature="services"><ServicesManager /></FeatureRoute>} />
            <Route path="staff" element={<FeatureRoute feature="staff"><StaffManager /></FeatureRoute>} />
            <Route path="customers" element={<CustomersManager />} />
            <Route path="availability" element={<FeatureRoute feature="availability"><AvailabilityManager /></FeatureRoute>} />
            <Route path="bookings" element={<FeatureRoute feature="bookings"><BookingsManager /></FeatureRoute>} />
            <Route path="newsletter" element={<FeatureRoute feature="newsletter"><NewsletterManager /></FeatureRoute>} />
            <Route path="email" element={<FeatureRoute feature="email_smtp"><EmailSettings /></FeatureRoute>} />
            <Route path="email-templates" element={<FeatureRoute feature="email_templates"><EmailTemplates /></FeatureRoute>} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="seo" element={<FeatureRoute feature="seo"><SeoManager /></FeatureRoute>} />
            <Route path="forms" element={<FeatureRoute feature="forms"><FormBuilder /></FeatureRoute>} />
            <Route path="loyalty" element={<FeatureRoute feature="loyalty"><LoyaltyManager /></FeatureRoute>} />
            <Route path="analytics" element={<FeatureRoute feature="analytics"><AnalyticsPage /></FeatureRoute>} />
          </Route>
          <Route path="/cms/pages/:id" element={<Protected><PageBuilder /></Protected>} />
          <Route path="/cms/onboarding" element={<Protected role="salon_admin"><OnboardingWizard /></Protected>} />
          <Route path="/__preview__" element={<BuilderPreview />} />

          <Route path="/salon/:slug" element={<PublicSalon />} />
          <Route path="/salon/:slug/account" element={<CustomerPortal />} />
          <Route path="/salon/:slug/blog/:postSlug" element={<BlogPostPage />} />
          <Route path="/salon/:slug/:pageSlug" element={<PublicSalon />} />

          {/* Public platform pages catch-all: /impressum, /datenschutz, /agb, /<custom-slug>.
              Must be LAST so all specific routes above win. */}
          <Route path="/:slug" element={<PublicPlatformPage />} />
        </Routes>
        <StudioMVBadge />
        <CookieConsent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
